export type ApplicationJourneyStepKey =
  | 'dashboard'
  | 'company-details'
  | 'credit-check'
  | 'farm-information'
  | 'production-info'
  | 'financials'
  | 'upload-documents'
  | 'agreement';

export interface IApplicationJourneySubStepDefinition {
  key: string;
  title: string;
  description: string;
}

export interface IApplicationJourneyStepDefinition {
  key: ApplicationJourneyStepKey;
  title: string;
  subtitle: string;
  path: string;
  description: string;
  subSteps?: IApplicationJourneySubStepDefinition[];
}

export const APPLICATION_JOURNEY_STEPS: IApplicationJourneyStepDefinition[] = [
  {
    key: 'dashboard',
    title: 'Dashboard',
    subtitle: 'Summary, performance & outstanding items',
    path: 'dashboard',
    description:
      'Track progress, key financials, production summaries, credit score and outstanding items in one place.',
  },
  {
    key: 'company-details',
    title: 'Company details',
    subtitle: 'Trading form, ownership & compliance',
    path: 'company',
    description:
      'Review the company profile, related parties, B-BBEE status and banking details for this application.',
    subSteps: [
      {
        key: 'registration',
        title: 'Verify your company registration',
        description:
          'Enter your CIPC registration number so we can confirm your business details.',
      },
      {
        key: 'details',
        title: 'Confirm these details',
        description:
          "Review the company information we retrieved and make sure it's correct.",
      },
      {
        key: 'parties',
        title: "Confirm your company's related parties",
        description:
          "We've pulled key people linked to your business. Please confirm they're still accurate.",
      },
      {
        key: 'bbbee',
        title: "What's your B-BBEE status?",
        description:
          'Select the level that best reflects your current certification.',
      },
      {
        key: 'banking',
        title: 'Enter your banking details',
        description:
          "Provide the account we'll use for trading settlements and repayments.",
      },
    ],
  },
  {
    key: 'credit-check',
    title: 'Credit check',
    subtitle: 'Verify identity & pull credit score',
    path: 'credit',
    description:
      "We'll do a soft ID check that will not affect the score while preparing the application for review.",
  },
  {
    key: 'farm-information',
    title: 'Farm information',
    subtitle: 'Farm profile, water rights & property',
    path: 'production-history',
    description:
      'Review the farm profile, water rights, property, risks, customers, suppliers and team.',
    subSteps: [
      {
        key: 'commodity-selection',
        title: 'Commodity selection',
        description:
          'Select the commodities that reflect your farm information profile.',
      },
      {
        key: 'farm-overview',
        title: 'Farm overview',
        description:
          'Make sure the following information is correct before continuing.',
      },
      {
        key: 'water-rights',
        title: 'Water rights',
        description:
          'Capture your registered water-rights permits, usage and scheme details.',
      },
      {
        key: 'property',
        title: 'Property',
        description:
          'Capture each deed and land-use distribution used for your operation.',
      },
      {
        key: 'top-risks',
        title: 'Top risks',
        description:
          'Choose a risk, describe the issue, and outline how you plan to address it.',
      },
      {
        key: 'primary-customers',
        title: 'Primary customers',
        description:
          'Select your customers and choose the products they typically buy from your farm.',
      },
      {
        key: 'primary-suppliers',
        title: 'Primary suppliers',
        description:
          'Select your suppliers and choose the products you typically buy from them.',
      },
      {
        key: 'team',
        title: 'Your team',
        description:
          "Share your team's names and roles to complete your farm's profile.",
      },
    ],
  },
  {
    key: 'production-info',
    title: 'Production info',
    subtitle: 'Commodities, livestock, crops & performance',
    path: 'production-information',
    description:
      'Review the production commodities, plans and historic performance for the application.',
    subSteps: [
      {
        key: 'commodity-selection',
        title: 'Production commodities',
        description:
          'Select which commodities are included in the funding request.',
      },
      {
        key: 'livestock',
        title: 'Livestock',
        description:
          'Capture livestock groups, grading, quantities and sell-off details.',
      },
      {
        key: 'long-term-crops',
        title: 'Long-term crops',
        description:
          'Capture orchard or block details with projected and historic performance.',
      },
      {
        key: 'production-plan',
        title: 'Production plan',
        description:
          'Capture short-term crop plans with hectares, timing and pricing.',
      },
      {
        key: 'short-term-production-history',
        title: 'Short-term history',
        description:
          'Capture past short-term crop performance to validate delivery record.',
      },
    ],
  },
  {
    key: 'financials',
    title: 'Set up financials',
    subtitle: 'Revenue, costs, assets & liabilities',
    path: 'financials',
    description:
      'Review financial history, cashflow, assets, liabilities and the funding request summary.',
    subSteps: [
      {
        key: 'financial-summary',
        title: 'Financial History',
        description:
          'Review historic financial performance year by year and add notes where necessary.',
      },
      {
        key: 'assets',
        title: 'CAPEX and assets',
        description:
          'Capture equipment, infrastructure and capital assets linked to the operation.',
      },
      {
        key: 'liabilities',
        title: 'Existing liabilities',
        description:
          'Declare loans, credit facilities and outstanding balances to complete the profile.',
      },
      {
        key: 'cashflow',
        title: 'Cashflow Projections',
        description:
          'Capture projected revenue, production costs and operating expenses in one combined table.',
      },
      {
        key: 'funding-request',
        title: 'Funding request summary',
        description:
          'Select the costs to finance and confirm the expected funding timeline.',
      },
    ],
  },
  {
    key: 'upload-documents',
    title: 'Upload documents',
    subtitle: 'Upload supporting documentation',
    path: 'documents',
    description:
      'Review and upload all required application documents before submission.',
  },
  {
    key: 'agreement',
    title: 'Agreement',
    subtitle: 'Review & sign funding agreements',
    path: 'agreements',
    description:
      'Review the agreements required to complete and submit the application.',
  },
];
